"use client";

import { useState, useRef, useEffect } from "react";
import { useAppStore } from "@/store/app-store";
import { cn } from "@/lib/utils";
import {
  X,
  User,
  Bell,
  Shield,
  Palette,
  Volume2,
  Keyboard,
  LogOut,
  Camera,
  Check,
  Moon,
  Sun,
  Monitor,
  Loader2,
} from "lucide-react";

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserUpdate?: (user: any) => void;
}

const tabs = [
  { id: "profile", label: "Profile", icon: User },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "audio", label: "Audio & Video", icon: Volume2 },
  { id: "keybinds", label: "Keybinds", icon: Keyboard },
  { id: "privacy", label: "Privacy", icon: Shield },
];

const statusOptions = [
  { id: "online", label: "Online", color: "bg-green-500" },
  { id: "idle", label: "Idle", color: "bg-yellow-500" },
  { id: "dnd", label: "Do Not Disturb", color: "bg-red-500" },
  { id: "offline", label: "Invisible", color: "bg-zinc-500" },
];

export function UserSettingsModal({ isOpen, onClose, onUserUpdate }: UserSettingsModalProps) {
  const { user: storeUser } = useAppStore();
  const [activeTab, setActiveTab] = useState("profile");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [status, setStatus] = useState<"online" | "idle" | "dnd" | "offline">("online");
  const [customStatus, setCustomStatus] = useState("");
  const [theme, setTheme] = useState<"dark" | "light" | "system">("dark");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Notification settings
  const [notifDesktop, setNotifDesktop] = useState(true);
  const [notifSounds, setNotifSounds] = useState(true);
  const [notifPreviews, setNotifPreviews] = useState(false);
  const [notifMentionsOnly, setNotifMentionsOnly] = useState(false);
  
  // Audio settings
  const [inputVolume, setInputVolume] = useState(100);
  const [outputVolume, setOutputVolume] = useState(100);
  const [inputDevice, setInputDevice] = useState("default");
  const [outputDevice, setOutputDevice] = useState("default");
  
  // Privacy settings
  const [allowDMs, setAllowDMs] = useState(true);
  const [allowFriendRequests, setAllowFriendRequests] = useState(true);
  const [showActivity, setShowActivity] = useState(true);
  const [showOnlineStatus, setShowOnlineStatus] = useState(true);

  // Fetch current user data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetch("/api/auth/me")
        .then(res => res.json())
        .then(data => {
          if (data.user) {
            setUser(data.user);
            setDisplayName(data.user.display_name || "");
            setBio(data.user.bio || "");
            setStatus(data.user.status || "online");
            setCustomStatus(data.user.custom_status || "");
            setAvatarUrl(data.user.avatar_url || "");
          }
        })
        .catch(err => console.error("Failed to fetch user:", err));
      
      // Load theme from localStorage
      const savedTheme = localStorage.getItem("nexus-theme") as "dark" | "light" | "system" | null;
      if (savedTheme) {
        setTheme(savedTheme);
      }
      
      // Load notification settings
      const notifSettings = localStorage.getItem("nexus-notifications");
      if (notifSettings) {
        const parsed = JSON.parse(notifSettings);
        setNotifDesktop(parsed.desktop ?? true);
        setNotifSounds(parsed.sounds ?? true);
        setNotifPreviews(parsed.previews ?? false);
        setNotifMentionsOnly(parsed.mentionsOnly ?? false);
      }
      
      // Load audio settings
      const audioSettings = localStorage.getItem("nexus-audio");
      if (audioSettings) {
        const parsed = JSON.parse(audioSettings);
        setInputVolume(parsed.inputVolume ?? 100);
        setOutputVolume(parsed.outputVolume ?? 100);
        setInputDevice(parsed.inputDevice ?? "default");
        setOutputDevice(parsed.outputDevice ?? "default");
      }
      
      // Load privacy settings
      const privacySettings = localStorage.getItem("nexus-privacy");
      if (privacySettings) {
        const parsed = JSON.parse(privacySettings);
        setAllowDMs(parsed.allowDMs ?? true);
        setAllowFriendRequests(parsed.allowFriendRequests ?? true);
        setShowActivity(parsed.showActivity ?? true);
        setShowOnlineStatus(parsed.showOnlineStatus ?? true);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch("/api/users/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: displayName, bio, status, custom_status: customStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess("Profile updated successfully!");
        if (onUserUpdate) onUserUpdate(data.user);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to save");
      }
    } catch (err) {
      setError("Failed to save profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const res = await fetch("/api/users/me/avatar", {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        setAvatarUrl(data.avatarUrl);
        setSuccess("Avatar updated!");
        if (onUserUpdate) onUserUpdate(data.user);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to upload avatar");
      }
    } catch (err) {
      setError("Failed to upload avatar");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
      window.location.href = "/login";
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl h-[600px] mx-4 bg-zinc-800 rounded-2xl shadow-2xl overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-56 bg-zinc-900 border-r border-zinc-700 p-4 flex flex-col">
          <h2 className="text-lg font-bold mb-4 px-2">Settings</h2>
          
          <nav className="flex-1 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    activeTab === tab.id
                      ? "bg-violet-600/20 text-violet-400"
                      : "hover:bg-zinc-700 text-zinc-400"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="pt-4 border-t border-zinc-700">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-700">
            <h3 className="text-xl font-bold capitalize">{activeTab}</h3>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "profile" && (
              <div className="space-y-6">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />

                {/* Success/Error messages */}
                {error && (
                  <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}

                {/* Avatar */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Avatar" 
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">
                          {displayName?.charAt(0) || user?.display_name?.charAt(0) || "U"}
                        </span>
                      </div>
                    )}
                    <button 
                      onClick={handleAvatarClick}
                      disabled={isUploading}
                      className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white hover:bg-violet-500 transition-colors disabled:opacity-50"
                    >
                      {isUploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div>
                    <h4 className="font-semibold">{displayName || user?.display_name || "User"}</h4>
                    <p className="text-sm text-zinc-400">@{user?.username || "user"}</p>
                    <p className="text-xs text-zinc-500 mt-1">Click camera to change avatar</p>
                  </div>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 transition-all outline-none text-white"
                  />
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium mb-2">About Me</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 transition-all resize-none outline-none text-white"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium mb-3">Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {statusOptions.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setStatus(opt.id as "online" | "idle" | "dnd" | "offline")}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all",
                          status === opt.id
                            ? "border-violet-500 bg-violet-500/10"
                            : "border-zinc-600 hover:border-zinc-500"
                        )}
                      >
                        <div className={cn("w-3 h-3 rounded-full", opt.color)} />
                        <span className="text-sm font-medium">{opt.label}</span>
                        {status === opt.id && (
                          <Check className="w-4 h-4 ml-auto text-violet-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Status */}
                <div>
                  <label className="block text-sm font-medium mb-2">Custom Status</label>
                  <input
                    type="text"
                    value={customStatus}
                    onChange={(e) => setCustomStatus(e.target.value)}
                    placeholder="What are you up to?"
                    className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 transition-all outline-none text-white"
                  />
                </div>
              </div>
            )}

            {activeTab === "appearance" && (
              <div className="space-y-6">
                {/* Success/Error messages */}
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}

                {/* Theme */}
                <div>
                  <label className="block text-sm font-medium mb-3">Theme</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: "dark", label: "Dark", icon: Moon },
                      { id: "light", label: "Light", icon: Sun },
                      { id: "system", label: "System", icon: Monitor },
                    ].map((t) => {
                      const Icon = t.icon;
                      return (
                        <button
                          key={t.id}
                          onClick={() => {
                            setTheme(t.id as any);
                            localStorage.setItem("nexus-theme", t.id);
                            // Apply theme
                            if (t.id === "dark" || (t.id === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)) {
                              document.documentElement.classList.add("dark");
                            } else if (t.id === "light") {
                              document.documentElement.classList.remove("dark");
                            }
                            setSuccess("Theme updated!");
                            setTimeout(() => setSuccess(null), 2000);
                          }}
                          className={cn(
                            "flex flex-col items-center gap-2 p-4 rounded-lg border transition-all",
                            theme === t.id
                              ? "border-violet-500 bg-violet-500/10"
                              : "border-zinc-600 hover:border-zinc-500"
                          )}
                        >
                          <Icon className="w-6 h-6" />
                          <span className="text-sm font-medium">{t.label}</span>
                          {theme === t.id && <Check className="w-4 h-4 text-violet-500" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Accent Color */}
                <div>
                  <label className="block text-sm font-medium mb-3">Accent Color</label>
                  <div className="flex gap-2">
                    {["#8B5CF6", "#EC4899", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"].map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          localStorage.setItem("nexus-accent", color);
                          document.documentElement.style.setProperty("--accent-color", color);
                          setSuccess("Accent color updated!");
                          setTimeout(() => setSuccess(null), 2000);
                        }}
                        className="w-10 h-10 rounded-full hover:scale-110 transition-transform ring-2 ring-offset-2 ring-offset-zinc-800 ring-transparent hover:ring-white/50"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "notifications" && (
              <div className="space-y-4">
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Enable Desktop Notifications</span>
                    <p className="text-sm text-zinc-400">Receive notifications even when the app is minimized</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !notifDesktop;
                      setNotifDesktop(newVal);
                      if (newVal && "Notification" in window) {
                        Notification.requestPermission();
                      }
                      localStorage.setItem("nexus-notifications", JSON.stringify({
                        desktop: newVal, sounds: notifSounds, previews: notifPreviews, mentionsOnly: notifMentionsOnly
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", notifDesktop ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", notifDesktop ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Enable Sounds</span>
                    <p className="text-sm text-zinc-400">Play sounds for messages and notifications</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !notifSounds;
                      setNotifSounds(newVal);
                      localStorage.setItem("nexus-notifications", JSON.stringify({
                        desktop: notifDesktop, sounds: newVal, previews: notifPreviews, mentionsOnly: notifMentionsOnly
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", notifSounds ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", notifSounds ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Enable Message Previews</span>
                    <p className="text-sm text-zinc-400">Show message content in notifications</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !notifPreviews;
                      setNotifPreviews(newVal);
                      localStorage.setItem("nexus-notifications", JSON.stringify({
                        desktop: notifDesktop, sounds: notifSounds, previews: newVal, mentionsOnly: notifMentionsOnly
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", notifPreviews ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", notifPreviews ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Notify on Mentions Only</span>
                    <p className="text-sm text-zinc-400">Only receive notifications when you are mentioned</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !notifMentionsOnly;
                      setNotifMentionsOnly(newVal);
                      localStorage.setItem("nexus-notifications", JSON.stringify({
                        desktop: notifDesktop, sounds: notifSounds, previews: notifPreviews, mentionsOnly: newVal
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", notifMentionsOnly ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", notifMentionsOnly ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
              </div>
            )}

            {activeTab === "audio" && (
              <div className="space-y-6">
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">Input Device</label>
                  <select
                    value={inputDevice}
                    onChange={(e) => {
                      setInputDevice(e.target.value);
                      localStorage.setItem("nexus-audio", JSON.stringify({
                        inputVolume, outputVolume, inputDevice: e.target.value, outputDevice
                      }));
                    }}
                    className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 outline-none text-white"
                  >
                    <option value="default">Default - System Microphone</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Input Volume</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={inputVolume}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setInputVolume(val);
                        localStorage.setItem("nexus-audio", JSON.stringify({
                          inputVolume: val, outputVolume, inputDevice, outputDevice
                        }));
                      }}
                      className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
                    />
                    <span className="w-12 text-right text-sm text-zinc-400">{inputVolume}%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Output Device</label>
                  <select
                    value={outputDevice}
                    onChange={(e) => {
                      setOutputDevice(e.target.value);
                      localStorage.setItem("nexus-audio", JSON.stringify({
                        inputVolume, outputVolume, inputDevice, outputDevice: e.target.value
                      }));
                    }}
                    className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-600 focus:border-violet-500 outline-none text-white"
                  >
                    <option value="default">Default - System Speakers</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Output Volume</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={outputVolume}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setOutputVolume(val);
                        localStorage.setItem("nexus-audio", JSON.stringify({
                          inputVolume, outputVolume: val, inputDevice, outputDevice
                        }));
                      }}
                      className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
                    />
                    <span className="w-12 text-right text-sm text-zinc-400">{outputVolume}%</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-zinc-700">
                  <button
                    onClick={() => {
                      setSuccess("Microphone test started!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 font-medium transition-colors"
                  >
                    Test Microphone
                  </button>
                </div>
              </div>
            )}

            {activeTab === "keybinds" && (
              <div className="space-y-4">
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}
                <p className="text-sm text-zinc-400 mb-4">Click on a keybind to change it. Press Escape to cancel.</p>
                {[
                  { action: "Toggle Mute", key: "Ctrl + M" },
                  { action: "Toggle Deafen", key: "Ctrl + D" },
                  { action: "Push to Talk", key: "Space" },
                  { action: "Open Settings", key: "Ctrl + ," },
                  { action: "Search", key: "Ctrl + K" },
                  { action: "Mark as Read", key: "Escape" },
                  { action: "Upload File", key: "Ctrl + U" },
                ].map((bind, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                    <span className="font-medium">{bind.action}</span>
                    <button className="px-3 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-sm font-mono transition-colors">
                      {bind.key}
                    </button>
                  </div>
                ))}
                <div className="pt-4">
                  <button
                    onClick={() => {
                      setSuccess("Keybinds reset to default!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 font-medium transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
              </div>
            )}

            {activeTab === "privacy" && (
              <div className="space-y-4">
                {success && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
                    {success}
                  </div>
                )}
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Allow Direct Messages</span>
                    <p className="text-sm text-zinc-400">Allow other users to send you direct messages</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !allowDMs;
                      setAllowDMs(newVal);
                      localStorage.setItem("nexus-privacy", JSON.stringify({
                        allowDMs: newVal, allowFriendRequests, showActivity, showOnlineStatus
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", allowDMs ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", allowDMs ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Allow Friend Requests</span>
                    <p className="text-sm text-zinc-400">Allow other users to send you friend requests</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !allowFriendRequests;
                      setAllowFriendRequests(newVal);
                      localStorage.setItem("nexus-privacy", JSON.stringify({
                        allowDMs, allowFriendRequests: newVal, showActivity, showOnlineStatus
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", allowFriendRequests ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", allowFriendRequests ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Show Current Activity</span>
                    <p className="text-sm text-zinc-400">Display what you're currently doing to others</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !showActivity;
                      setShowActivity(newVal);
                      localStorage.setItem("nexus-privacy", JSON.stringify({
                        allowDMs, allowFriendRequests, showActivity: newVal, showOnlineStatus
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", showActivity ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", showActivity ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900">
                  <div>
                    <span className="font-medium">Show Online Status</span>
                    <p className="text-sm text-zinc-400">Let others see when you're online</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !showOnlineStatus;
                      setShowOnlineStatus(newVal);
                      localStorage.setItem("nexus-privacy", JSON.stringify({
                        allowDMs, allowFriendRequests, showActivity, showOnlineStatus: newVal
                      }));
                      setSuccess("Settings saved!");
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                    className={cn("w-12 h-6 rounded-full transition-colors relative", showOnlineStatus ? "bg-violet-600" : "bg-zinc-600")}
                  >
                    <div className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", showOnlineStatus ? "translate-x-7" : "translate-x-1")} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {activeTab === "profile" && (
            <div className="p-6 border-t border-zinc-700 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-xl bg-zinc-700 hover:bg-zinc-600 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
